/**
 * Client Data Structure and Interfaces
 * 
 * This file contains all the client/portfolio data for the Involved website.
 * Each client has images, videos, descriptions, and metadata that gets rendered
 * in the accordion components.
 */

/**
 * Interface for individual client images with descriptive alt text
 */
export interface ClientImage {
  src: string;
  alt: string;
  type?: 'image' | 'video';
  videoSrc?: string;
}

/**
 * Interface for client video assets with poster and descriptive alt text
 */
export interface ClientVideo {
  src: string;
  poster?: string;
  alt?: string;
}

/**
 * Main Client interface defining the structure of client data
 */
export interface Client {
  id: string;                    // Unique identifier for the client
  name: string;                  // Display name of the client
  icon: string;                  // FontAwesome icon class name
  description: string;           // Client description and work details
  website?: string;              // Optional website URL (without https://)
  credit?: string;               // Optional photo/video credits
  images: (string | { src: string; alt: string })[]; // Array of image file paths or objects with alt text
  videos?: ClientVideo[];        // Optional video files with posters and alt descriptions
  categories?: string[];         // Optional categories for filtering/styling
}

/**
 * Client data array containing all portfolio items
 * 
 * Each client object contains:
 * - Basic info (name, description, website)
 * - Media assets (images and videos with rich descriptive alt text)
 * - Metadata (credits, categories)
 */
export const clients: Client[] = [
  {
    id: 'accor',
    name: 'Accor',
    icon: 'fa-suitcase',
    description: 'Digital creative, production and web pages for global hotel brand across Asia Pacific. 2019—2022.',
    website: 'accor.com',
    images: [
      { src: '/images/accor/accor_1.jpg', alt: 'Aerial night view of Manhattan city skyline with gold Accor branding logo' },
      { src: '/images/accor/accor_2.jpg', alt: 'Accor Live Limitless conference presentation on stage with large blue backdrop' },
      { src: '/images/accor/accor_3.jpg', alt: 'Lionel Messi celebrating in Paris Saint-Germain jersey with ALL Accor Live Limitless sponsor logo' },
      { src: '/images/accor/accor_4.jpg', alt: 'Australian Open tennis arena bathed in sunlight with ALL Accor Live Limitless court banners' },
      { src: '/images/accor/accor_5.jpg', alt: 'Onefinestay luxury villa interior overlooking scenic mountains with quote for AccorHotels' },
      { src: '/images/accor/accor_6.jpg', alt: 'Airport digital billboard advertisement for MyResorts Le Club AccorHotels Asia Pacific' },
      { src: '/images/accor/accor_7.jpg', alt: 'Outdoor roadside billboard and illuminated bus shelter ads for MyResorts Le Club AccorHotels at night' },
      { src: '/images/accor/accor_8.jpg', alt: 'Pullman Hotels and Resorts digital billboard screen above airport departures terminal: OUR WORLD IS YOUR PLAYGROUND' },
      { src: '/images/accor/accor_9.jpg', alt: 'Four tablet mockups showing Pullman Hotels Our World is Your Playground digital campaign landing screens' },
      { src: '/images/accor/accor_10.jpg', alt: 'Pullman Hotels promotional banner with woman reclining beside an infinity pool at sunset' },
      { src: '/images/accor/accor_11.jpg', alt: 'Pullman Hotels promotional banner featuring sophisticated modern lounge interior' },
      { src: '/images/accor/accor_12.jpg', alt: 'Pullman Hotels mobile responsive website mockups on smartphone screens' },
      { src: '/images/accor/accor_13.jpg', alt: 'Pullman Hotels digital campaign display screen inside a modern hotel lobby' },
      { src: '/images/accor/accor_14.jpg', alt: 'Pullman Hotels luxury suite interior photograph with ambient lighting' },
      { src: '/images/accor/accor_15.jpg', alt: 'Pullman Hotels stylish rooftop bar and pool scene with sunset skyline' },
      { src: '/images/accor/accor_16.jpg', alt: 'Pullman Hotels restaurant dining and culinary branding campaign asset' },
      { src: '/images/accor/accor_17.jpg', alt: 'Pullman Hotels brand campaign billboard overlooking city street' },
      { src: '/images/accor/accor_19.jpg', alt: 'Accor Plus membership benefits campaign banner for Asia Pacific hotels' },
      { src: '/images/accor/accor_20.jpg', alt: 'Accor Plus digital marketing promotional email and web creative' },
      { src: '/images/accor/accor_21.jpg', alt: 'Accor Plus luxury resort escape promotional creative' },
      { src: '/images/accor/accor_23.jpg', alt: 'Accor Live Limitless lifestyle loyalty campaign visual asset' },
      { src: '/images/accor/accor_24.jpg', alt: 'Accor Live Limitless digital interactive experience landing screen' }
    ],
    videos: [
      { src: '/images/accor/accor_18.mp4', poster: '/images/accor/accor_18-poster.jpg', alt: "Pullman Hotels 'Our World is Your Playground' video campaign animation" },
      { src: '/images/accor/accor_22.mp4', poster: '/images/accor/accor_22-poster.jpg', alt: 'Accor Plus membership promotional video showcase' },
      { src: '/images/accor/accor_25.mp4', poster: '/images/accor/accor_25-poster.jpg', alt: 'Accor Live Limitless brand video reel' }
    ]
  },
  {
    id: 'ancap',
    name: 'Australasian New Car Assessment Program (ANCAP)',
    icon: 'fa-road',
    description: 'Strategy, collateral, campaigns and bespoke digital platform for ANCAP, Australasia’s leading vehicle safety authority. 2013—ongoing',
    website: 'ancap.com.au',
    images: [
      { src: '/images/ancap/ancap0.jpg', alt: 'Young child sleeping in a car seat hugging a plush rabbit with ANCAP Safety tagline: SAFER VEHICLES FOR ALL' },
      { src: '/images/ancap/ancap1.jpg', alt: 'Overhead view of head-on vehicle crash test comparison between 1998 and 2015 Toyota Corolla for ANCAP Safety' },
      { src: '/images/ancap/ancap2.jpg', alt: 'Bespoke digital platform UI layouts for ANCAP Safety including homepage search, vehicle rating grid, and Mazda CX-5 safety score breakdown' },
      { src: '/images/ancap/ancap3.jpg', alt: 'ANCAP Safety rating badge system showing 5-star safety criteria and test results' },
      { src: '/images/ancap/ancap4.jpg', alt: 'Mobile responsive web app screens for ANCAP vehicle safety search and comparison' },
      { src: '/images/ancap/ancap5.jpg', alt: 'ANCAP vehicle crash laboratory testing environment with high-speed cameras and test dummy' },
      { src: '/images/ancap/ancap6.jpg', alt: 'ANCAP pedestrian safety impact testing simulation and data visualizations' },
      { src: '/images/ancap/ancap7.jpg', alt: 'ANCAP annual safety report editorial publication layout and infographic spreads' },
      { src: '/images/ancap/ancap8.jpg', alt: 'ANCAP community awareness campaign poster highlighting active collision avoidance systems' },
      { src: '/images/ancap/ancap9.jpg', alt: 'ANCAP commercial van and utility vehicle safety ratings campaign graphics' }
    ],
    videos: [
      { src: '/images/ancap/ancap10.mp4', poster: '/images/ancap/ancap10-poster.jpg', alt: 'ANCAP vehicle crash test slow-motion video demonstration' }
    ]
  },
  {
    id: 'aiac',
    name: 'Australian International Aviation College',
    icon: 'fa-paper-plane',
    description: 'Art direction, design and development for international flight training facility based on the NSW mid-north coast. 2013—ongoing.',
    website: 'aiacollege.com.au',
    credit: 'Photography: Anne Johnston, AIAC students.',
    images: [
      { src: '/images/aiac/aiac0.jpg', alt: 'Two student pilots smiling and giving thumbs up inside the cockpit of an AIAC training aircraft in flight over the coast' },
      { src: '/images/aiac/aiac1.jpg', alt: 'Tablet responsive web design mockups for Australian International Aviation College showing homepage and Commercial Pilot Licence diploma course details' },
      { src: '/images/aiac/aiac2.jpg', alt: 'AIAC flight training fleet aircraft lined up on the airport tarmac at sunrise' },
      { src: '/images/aiac/aiac3.jpg', alt: 'AIAC flight instructor briefing a student pilot over flight navigation instruments' },
      { src: '/images/aiac/aiac4.jpg', alt: 'AIAC Diamond aircraft soaring in mid-air above the coastline' },
      { src: '/images/aiac/aiac5.jpg', alt: 'AIAC student pilot in uniform preparing for a solo cross-country training flight' },
      { src: '/images/aiac/aiac6.jpg', alt: 'Aviation college admissions prospectus booklet and brand collateral' },
      { src: '/images/aiac/aiac7.jpg', alt: 'Modern flight simulation laboratory with student training in full cockpit simulator' },
      { src: '/images/aiac/aiac8.jpg', alt: 'Graduating commercial airline pilots celebrating on the tarmac at AIAC campus' }
    ]
  },
  {
    id: 'art-gallery-ballarat',
    name: 'Art Gallery of Ballarat',
    icon: 'fa-palette',
    description: 'Design and build of an online exhibition showcasing The Vizard Foundation Art Collection of the 1990s. This immersive website features 124 works by 51 prominent Australian artists. 2024—ongoing.',
    website: 'vizardfoundationartcollection.com.au/the-nineties',
    images: [
      { src: '/images/art-gallery-ballarat/ballarat0.jpg', alt: 'Art Gallery of Ballarat exhibition room displaying The Vizard Foundation Art Collection of the 1990s' },
      { src: '/images/art-gallery-ballarat/ballarat1.jpg', alt: 'Online digital exhibition interactive web platform showcasing 1990s Australian art collection with artist profiles and audio guides' }
    ]
  },
  {
    id: 'box-grove-vineyard',
    name: 'Box Grove Vineyard',
    icon: 'fa-glass-cheers',
    description: 'Strategy, digital and marketing for award-winning boutique vineyard specialising in Rhone and Italian varietals. Avenel, Victoria. 2007—ongoing.',
    website: 'boxgrovevineyard.com.au',
    categories: ['shopify'], // Category for Shopify-based projects
    images: [
      { src: '/images/box-grove-vineyard/bgv0.jpg', alt: 'Winemaker and companion walking with small dog down a sunny vineyard row at Box Grove Vineyard' },
      { src: '/images/box-grove-vineyard/bgv1.jpg', alt: 'Box Grove Vineyard bespoke Shopify ecommerce store homepage and wine collection catalog' },
      { src: '/images/box-grove-vineyard/bgv2.jpg', alt: 'Close-up of Box Grove Vineyard wine bottles featuring custom illustrated labels' },
      { src: '/images/box-grove-vineyard/bgv3.jpg', alt: 'Vineyard rows during autumn harvest with golden grape leaves in Avenel, Victoria' },
      { src: '/images/box-grove-vineyard/bgv4.jpg', alt: 'Tasting room cellar door setting with curated wine glasses and cheese pairing' },
      { src: '/images/box-grove-vineyard/bgv5.jpg', alt: 'Box Grove Vineyard sparkling wine packaging and bottle photography' },
      { src: '/images/box-grove-vineyard/bgv6.jpg', alt: 'Boutique Italian and Rhone varietal wine selection display' },
      { src: '/images/box-grove-vineyard/bgv7.jpg', alt: 'Winemaker inspecting grape clusters on the vine during ripening season' },
      { src: '/images/box-grove-vineyard/bgv8.jpg', alt: 'Mobile responsive Shopify checkout experience for Box Grove wine club' },
      { src: '/images/box-grove-vineyard/bgv9.jpg', alt: 'Scenic aerial landscape of Box Grove Vineyard property and surrounding hills' },
      { src: '/images/box-grove-vineyard/bgv10.jpg', alt: 'Box Grove Vineyard promotional postcard and seasonal tasting menu' }
    ]
  },
  {
    id: 'designsource',
    name: 'Designsource',
    icon: 'fa-cut',
    description: 'Website for agency specialising in fabric sourcing, design consultancy, garment development, production management and fashion branding.',
    website: 'designsource.com.au',
    credit: 'Photography: Eve Wilson',
    images: [
      { src: '/images/designsource/ds0.jpg', alt: 'Stack of textured fabric swatches, tailoring pins, and antique shears for Designsource textile agency' },
      { src: '/images/designsource/ds1.jpg', alt: 'Minimalist website layout for Designsource showcasing fabric sourcing and garment consultancy services' },
      { src: '/images/designsource/ds2.jpg', alt: 'Collection of dyed denim and cotton textile swatches arranged by texture' },
      { src: '/images/designsource/ds3.jpg', alt: 'Garment development technical sketch and fabric specification sheet' },
      { src: '/images/designsource/ds4.jpg', alt: 'Fashion studio showroom display with curated clothing samples on hangers' },
      { src: '/images/designsource/ds5.jpg', alt: 'Close-up of artisanal woven fabric patterns and yarn details' },
      { src: '/images/designsource/ds6.jpg', alt: 'Designsource Fitzroy studio workspace with moodboards and production textiles' }
    ]
  },
  {
    id: 'gristmill',
    name: 'Gristmill',
    icon: 'fa-tv-retro',
    description: 'An immersive website showcasing the work of Gristmill, an award-winning production company founded by Wayne Hope and Robyn Butler. 2013—ongoing.',
    website: 'gristmill.com.au',
    images: [
      { src: '/images/gristmill/gristmill_2.jpg', alt: 'Gristmill founders Robyn Butler and Wayne Hope standing together on coral background with AACTA award badges' },
      { src: '/images/gristmill/gristmill_3.jpg', alt: 'Gristmill website portfolio page showing television comedy and drama productions including Upper Middle Bogan and Little Lunch' },
      { src: '/images/gristmill/gristmill_4.png', alt: 'Interactive show details page with production gallery, cast interviews, and broadcast streaming links' },
      { src: '/images/gristmill/gristmill_5.jpg', alt: 'Behind-the-scenes film crew and cast on location during a Gristmill television shoot' }
    ],
    videos: [
      { src: '/images/gristmill/gristmill_1.mp4', poster: '/images/gristmill/gristmill_poster.jpg', alt: 'Gristmill television production showreel highlighting award-winning comedy series' }
    ]
  },
  // {
  //   id: 'mastercard',
  //   name: 'Mastercard®',
  //   icon: 'fa-credit-card',
  //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aliquam eget iaculis lacus. In eu euismod massa. Nam metus enim, ornare sed dui at, condimentum dictum ex. Aenean sollicitudin fermentum arcu sed tristique. Quisque consectetur efficitur odio.',
  //   categories: ['no-content'], // Category for placeholder content
  //   images: [
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png'
  //   ]
  // },
  {
    id: 'motorola',
    name: 'Motorola UK',
    icon: 'fa-mobile-android-alt',
    description: 'Digital creative and production for Razr and Edge product launches across Europe. 2020—2022.',
    website: 'motorola.co.uk',
    images: [
      { src: '/images/motorola/motorola4.jpg', alt: 'Motorola Edge smartphone product showcase showing sleek curved display with vibrant smoke visuals and typography' },
      { src: '/images/motorola/motorola5.jpg', alt: 'Motorola Razr foldable smartphone digital advertising campaign visual with modern edge lighting' }
    ],
    videos: [
      { src: '/images/motorola/motorola3.mp4', poster: '/images/motorola/motorola3-poster.jpg', alt: 'Motorola Razr flip animation and hardware feature showcase video' },
      { src: '/images/motorola/motorola2.mp4', poster: '/images/motorola/motorola2-poster.jpg', alt: 'Motorola Edge camera and display cinematic video teaser' },
      { src: '/images/motorola/motorola6.mp4', poster: '/images/motorola/motorola6-poster.jpg', alt: 'Motorola product campaign motion graphics reel' }
    ]
  },
  {
    id: 'melanie-katsalidis',
    name: 'Melanie Katsalidis',
    icon: 'fa-gem',
    description: 'Commerce platform development and support for renowned Melbourne jeweller and designer. Customised Shopify and Mailchimp. Since 2006.',
    website: 'melaniekatsalidis.com',
    credit: 'Brand: Mike Giesser.',
    categories: ['shopify'], // Category for Shopify-based projects
    images: [
      { src: '/images/melanie-katsalidis/mk0.jpg', alt: 'Fine bespoke gemstone ring by Melanie Katsalidis resting on sculptural marble and coloured acrylic blocks' },
      { src: '/images/melanie-katsalidis/mk1.jpg', alt: 'Melanie Katsalidis custom Shopify ecommerce store displaying designer jewellery collections' },
      { src: '/images/melanie-katsalidis/mk2.jpg', alt: 'Handcrafted diamond and precious metal necklace on architectural display stand' },
      { src: '/images/melanie-katsalidis/mk3.jpg', alt: 'Melanie Katsalidis jewellery boutique showroom in Melbourne with curated glass display cases' },
      { src: '/images/melanie-katsalidis/mk4.jpg', alt: 'Detail photograph of artisanal gold rings with geometric gemstone cuts' }
    ]
  },
  // {
  //   id: 'pieces-of-eight',
  //   name: 'Pieces of Eight',
  //   icon: 'fa-rings-wedding',
  //   description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aliquam eget iaculis lacus. In eu euismod massa. Nam metus enim, ornare sed dui at, condimentum dictum ex. Aenean sollicitudin fermentum arcu sed tristique. Quisque consectetur efficitur odio.',
  //   website: 'piecesofeight.com.au',
  //   categories: ['shopify', 'no-content'], // Categories for Shopify and placeholder content
  //   images: [
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png',
  //     '/images/big.png'
  //   ]
  // },
  {
    id: 'potter-museum',
    name: 'Potter Museum',
    icon: 'fa-paint-brush-alt',
    description: 'Design and build of exhibition apps for the University of Melbourne\'s art museum. 2014—ongoing.',
    images: [
      { src: '/images/potter-museum/potter0.jpg', alt: 'iPad app interface design for Potter Museum Basil Sellers Art Prize exhibition featuring underwater swimmer imagery and artist index' },
      { src: '/images/potter-museum/potter1.jpg', alt: 'Interactive touchscreen kiosk app showcasing exhibition artworks, curatorial essays, and artist biographies' },
      { src: '/images/potter-museum/potter5.jpg', alt: 'Potter Museum exhibition gallery space with visitors exploring interactive digital displays' },
      { src: '/images/potter-museum/potter6.jpg', alt: 'Tablet application artwork zoom and high-resolution detail viewer interface' },
      { src: '/images/potter-museum/potter7.jpg', alt: 'Basil Sellers Art Prize digital exhibition catalog layout and multimedia archives' }
    ]
  },
  {
    id: 'fishmongers-son',
    name: 'The Fishmonger\'s Son',
    icon: 'fa-fish',
    description: 'Branding, signage and marketing for local seafood deli, The Fishmonger\'s Son. 2018—ongoing.',
    images: [
      { src: '/images/fishmongers-son/tfs1.jpg', alt: 'The Fishmonger\'s Son brand packaging collateral featuring printed stickers, brown craft paper wrapping, and fish illustration cards' },
      { src: '/images/fishmongers-son/tfs2.jpg', alt: 'The Fishmonger\'s Son storefront signage and window decal design in Melbourne' },
      { src: '/images/fishmongers-son/tfs3.jpg', alt: 'Fresh artisan seafood display on crushed ice at The Fishmonger\'s Son counter' },
      { src: '/images/fishmongers-son/tfs4.jpg', alt: 'Branded takeaway packaging bags, printed menu cards, and branded deli butcher paper' }
    ]
  },
  {
    id: 'holistic-ingrediant',
    name: 'The Holistic Ingredient',
    icon: 'fa-spa',
    description: 'Bespoke package design, custom Shopify development, strategic direction and ongoing support for Amy Crawford, creator of The Holistic Ingredient lifestyle store and daily blends range. 2013—ongoing.',
    website: 'theholisticingredient.com',
    categories: ['shopify'], // Category for Shopify-based projects
    images: [
      { src: '/images/holistic-ingrediant/amy_1.jpg', alt: 'The Holistic Ingredient homepage hero featuring founder Amy Crawford relaxing in living room with smoothie and dog, with tagline body, mind, home' },
      { src: '/images/holistic-ingrediant/amy_2.jpg', alt: 'Custom Shopify ecommerce store interface for The Holistic Ingredient lifestyle products and organic daily blends' },
      { src: '/images/holistic-ingrediant/amy_3.jpg', alt: 'Product packaging mockup for The Holistic Ingredient superfood blends with clean botanical typography' },
      { src: '/images/holistic-ingrediant/amy_4.jpg', alt: 'Editorial recipe blog and wellness lifestyle article layout on The Holistic Ingredient website' }
    ]
  },
  {
    id: 'westfield',
    name: 'Westfield',
    icon: 'fa-building',
    description: 'Digital creative support for Westfield Direct launch and seasonal campaigns. 2020—2022. With Sibling.',
    website: 'westfield.com.au',
    images: [
      { src: '/images/westfield/westfield0.jpg', alt: 'Westfield Direct campaign visual with joyful woman surrounded by floating summer fashion garments' },
      { src: '/images/westfield/westfield2.jpg', alt: 'Westfield Direct digital advertising banner series for seasonal retail promotions across desktop and mobile' },
      { src: '/images/westfield/westfield4.jpg', alt: 'Westfield Direct ecommerce landing page with curated product categories and click-and-collect highlights' }
    ],
    videos: [
      { src: '/images/westfield/westfield1.mp4', poster: '/images/westfield/westfield1-poster.jpg', alt: 'Westfield Direct launch campaign motion ad featuring floating fashion items' },
      { src: '/images/westfield/westfield3.mp4', poster: '/images/westfield/westfield3-poster.jpg', alt: 'Westfield Direct seasonal sale animated digital display ad' },
      { src: '/images/westfield/westfield5.mp4', poster: '/images/westfield/westfield5-poster.jpg', alt: 'Westfield Direct shopping experience promotional video clip' }
    ]
  },
  {
    id: 'kms-for-kids',
    name: '1200kms for Kids',
    icon: 'fa-biking',
    description: 'Pro-bono design and development support for annual 1,200 kilometre east coast charity ride, raising funds for the Children\'s Hospital Foundation. 2009—ongoing.',
    website: '1200kmsforkids.com',
    images: [
      { src: '/images/1200kms-for-kids/kids0.jpg', alt: 'Three cyclists in 1200kms for Kids charity jerseys riding along a country road at golden sunrise' },
      { src: '/images/1200kms-for-kids/kids1.jpg', alt: '1200kms for Kids charity ride website homepage with live fundraising tracker and rider registration' },
      { src: '/images/1200kms-for-kids/kids2.jpg', alt: 'Peloton of cyclists riding in formation along coastal highway during charity tour' },
      { src: '/images/1200kms-for-kids/kids3.jpg', alt: 'Charity riders celebrating at the finish line with Children\'s Hospital Foundation support team' },
      { src: '/images/1200kms-for-kids/kids4.jpg', alt: '1200kms for Kids branded cycling kit jersey design and commemorative event medals' },
      { src: '/images/1200kms-for-kids/kids5.jpg', alt: 'Riders smiling alongside young hospital patient ambassadors at community celebration event' }
    ]
  }
];